import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const reportConfigSlice = createSlice({
    name: 'reportConfig',
    initialState: {
        pending: false,
        savePending: false,
        reportConfigs: [],
        error: null,
        currentPage: 0,
        totalPages: 0,
        count: 0,
        total: 0,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        setReportConfigs: (state, action) => {
            state.pending = false;
            state.reportConfigs = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
            state.count = action.payload.count;
            state.total = action.payload.total;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        addReportConfig: (state, action) => {
            state.savePending = false;
            state.reportConfigs = [...state.reportConfigs, action.payload];
        },
        editReportConfig: (state, action) => {
            state.savePending = false;
            const index = state.reportConfigs.findIndex((reportConfig) => reportConfig.id === action.payload.id);
            if (index !== -1) {
                state.reportConfigs = [
                    ...state.reportConfigs.slice(0, index),
                    action.payload,
                    ...state.reportConfigs.slice(index + 1),
                ];
            }
        },
        deleteReportConfig: (state, action) => {
            state.pending = false;
            const index = state.reportConfigs.findIndex((reportConfig) => reportConfig.id === action.payload.id);
            if (index !== -1) {
                state.reportConfigs = [...state.reportConfigs.slice(0, index), ...state.reportConfigs.slice(index + 1)];
            }
        },
        resetReportConfigs: (state, action) => {
            state.reportConfigs = [];
            state.currentPage = 0;
            state.totalPages = 0;
            state.count = 0;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

export const {
    start,
    stop,
    addReportConfig,
    setReportConfigs,
    setError,
    editReportConfig,
    deleteReportConfig,
    resetReportConfigs,
    startSave,
    stopSave,
    setSaveError,
} = reportConfigSlice.actions;

export const getReportConfigs = (state) => state.reportConfig.reportConfigs;
export const getReportConfigsTotal = (state) => state.reportConfig.total;
export const getReportConfigsTotalPages = (state) => state.reportConfig.totalPages;
export const getReportConfigsCurrentPage = (state) => state.reportConfig.currentPage;
export const getReportConfigsPending = (state) => state.reportConfig.pending;
export const getReportConfigsError = (state) => state.reportConfig.error;
export const getReportConfigsSavePending = (state) => state.reportConfig.savePending;

export const fetchReportConfigsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            dispatch(start());
            let query = transformFiltersToQuery(queryParams);
            api.get('report-config' + query)
                .then((response) => {
                    dispatch(setReportConfigs(response.data));
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

const refetchList = () => (dispatch) => {
    dispatch(
        fetchReportConfigsAction({
            queryParams: {
                offset: 0,
                limit: 10,
                expand: ['organization'],
            },
        }),
    );
};

export const addReportConfigAction = (reportConfigDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSave());
        api.post(`report-config`, reportConfigDto)
            .then((response) => {
                dispatch(addReportConfig(response.data));
                dispatch(refetchList());
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const editReportConfigAction = (id, reportConfigDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSave());
        api.put('report-config/' + id, reportConfigDto)
            .then((response) => {
                dispatch(editReportConfig(response.data));
                dispatch(refetchList());
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const deleteReportConfigAction = (reportConfigId, success, failure) => async (dispatch) => {
    try {
        dispatch(start());
        api.delete(`report-config/${reportConfigId}`)
            .then((response) => {
                dispatch(deleteReportConfig(response.data));
                dispatch(refetchList());
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                dispatch(setError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stop());
        return console.error('error', e);
    }
};

export const previewReportConfigAction = (payload) => async () => {
    const response = await api.post('reports/preview', payload);
    return response.data;
};

export default reportConfigSlice.reducer;
